import { z } from 'zod';

/**
 * W3 OWNERSHIP SPINE — structured INCIDENT COMMAND (single command now; DESIGNED to
 * carry unified command next slice). Migration 0120 / Task #489 (W3 council #481).
 *
 * Ownership is a JOIN (incident_command), NOT a single owner column. Ontario IMS
 * Guidance 2.0 (docs/ontario-ims-doctrine-reference.md, extended #488):
 *   • SINGLE command (§4.5, the DEFAULT — this slice) = exactly ONE live participation
 *     (is_lead=true, unified=false). App A #74: "Single command exists when coordination
 *     and command of an incident response is the responsibility of a single organization
 *     or jurisdiction."
 *   • UNIFIED command (§4.7, the rare escalation — NEXT slice) = 2+ live participations,
 *     exactly one lead, a signed agreement. §4.7 unified command is ONE shared command
 *     (one IAP, one ops chief), never N separate ownerships (the drift §2.10 rejects).
 *   • The participant term is LEAD ORGANIZATION (§4.5 "the lead organization responsible
 *     for the incident response"). R1 'owned' = "Incident Command established."
 */

/* ------------------------------------------------------------------ *
 * Commanding-seat rule (§4.1 command / §4.2 command ≠ coordination)
 * ------------------------------------------------------------------ */

/**
 * The canonical jurisdiction tier that may hold INCIDENT COMMAND — a COMMANDING seat
 * (the Incident Commander of a lead jurisdiction). This is the {municipal, provincial,
 * first_nation, federal} subset of AGENCY_KINDS ('agency' / 'drone-operator' are
 * non-jurisdiction classes and cannot command). first_nation is a PEER tier (un-folded
 * from municipal by migration 0120), so a band council is a first-class lead organization.
 */
export const COMMANDING_JURISDICTIONS = [
  'municipal',
  'provincial',
  'first_nation',
  'federal',
] as const;
export type CommandingJurisdiction = (typeof COMMANDING_JURISDICTIONS)[number];

/**
 * A coordinating EOC / PEOC / CAF — or a federal SUPPORT department (ISC, App A #4/#82) —
 * must NEVER be modelled as the incident Owner (§4.2: "Support to the site is different
 * from coordination and command of the incident itself."; §2.9: "Involvement ≠ command.").
 * `agencies` carries no coordination/support flag, so this defensive NAME guard rejects
 * such a body even if one were seeded with a jurisdiction kind (ISC is kind='federal' —
 * accurate tier — yet a §4.2 support body, so it is guarded out here). (The §4.2
 * command-from-an-EOC exception for a site-less incident is out of scope this slice —
 * single command by a jurisdiction lead only.)
 */
const COORDINATION_BODY_PATTERNS: readonly RegExp[] = [
  // EOC / PEOC — an emergency operations centre COORDINATES; it does not command (§4.2).
  // (The §4.2 command-from-an-EOC exception for a site-less incident is out of scope this
  // slice — single command by a jurisdiction lead only.)
  /\bpeoc\b/i,
  /\beoc\b/i,
  /emergency operations cent(re|er)/i,
  /provincial emergency operations/i,
  // CAF / DND / the military — a §4.2 support body, never the incident Owner. DND is the
  // department CAF belongs to (the "federal DND that is really CAF" vector); JTF / division
  // formations are CAF echelons.
  /\bcaf\b/i,
  /canadian armed forces/i,
  /\barmed forces\b/i,
  /\bdnd\b/i,
  /department of national defence/i,
  /\bjoint task force\b/i,
  /\bjtf\b/i,
  // ISC (Indigenous Services Canada) — the federal department that FUNDS + coordinates
  // on-reserve emergency management (App A #4/#82 assisting/supporting; #52 mutual
  // assistance; PERP §6.7.1). Like CAF/DND it is a §4.2 SUPPORT body: a First Nation's
  // Chief-and-Council commands its own incident; ISC never assumes Incident Command. So ISC
  // is a valid agency_tasks DOWN-TASK counterparty (W4d/#519) but must never surface in the
  // commanding-seat (lead-organization) picker. Founder-authorized 2026-07-03 (Christopher).
  /indigenous services canada/i,
  /\bisc\b/i,
];

/** True when the agency NAME denotes a coordination OR federal-support body (PEOC / CAF /
 *  ISC) — a §4.2 body that supports but never holds Incident Command, so never a lead. */
export function isCoordinationBody(agencyName: string): boolean {
  return COORDINATION_BODY_PATTERNS.some((re) => re.test(agencyName));
}

/**
 * May this agency hold INCIDENT COMMAND (be a lead organization)? Doctrine §4.1/§4.5:
 * the owner is a commanding JURISDICTION seat AND not a coordinating EOC/PEOC/CAF
 * (§4.2/§2.9). Used by the assume-command route and pinned by the doctrine lock test.
 */
export function isCommandingSeat(agencyKind: string, agencyName: string): boolean {
  if (!COMMANDING_JURISDICTIONS.includes(agencyKind as CommandingJurisdiction)) return false;
  if (isCoordinationBody(agencyName)) return false;
  return true;
}

/* ------------------------------------------------------------------ *
 * Functional-role vocabulary (§4.7 Operations branches — next slice)
 * ------------------------------------------------------------------ */

/**
 * A command participation's functional branch (§4.7 Operations branches under the one
 * lead). NULL for the single lead this slice; the FK lookup valid_incident_functional_roles
 * (migration 0120) is the DB source of truth. Kept in lockstep with that seed.
 */
export const INCIDENT_FUNCTIONAL_ROLES = [
  'suppression',
  'community_protection',
  'structure_protection',
  'evacuation',
  'medical',
] as const;
export const IncidentFunctionalRoleSchema = z.enum(INCIDENT_FUNCTIONAL_ROLES);
export type IncidentFunctionalRole = z.infer<typeof IncidentFunctionalRoleSchema>;

/* ------------------------------------------------------------------ *
 * Wire contracts
 * ------------------------------------------------------------------ */

/**
 * One LIVE command participation — a lead organization + its command officer. N=1 this
 * slice (the single lead); the schema carries the next slice's unified-command co-leads.
 */
export const IncidentCommandParticipantSchema = z.object({
  agency_id: z.string(),
  /** The LEAD ORGANIZATION display name (§4.5). */
  agency_name: z.string(),
  /** Jurisdiction tier — drives the tier pill (first_nation renders first-class). */
  agency_kind: z.string(),
  is_lead: z.boolean(),
  /** §4.7 unified — true when 2+ lead organizations share command (unified command). */
  unified: z.boolean(),
  /** §4.7 Operations branch under the one lead (Suppression / Community Protection / …). */
  functional_role: IncidentFunctionalRoleSchema.nullable(),
  /** §4.7 the signed agreement to unified command ("verbally stated and signed in the written
   *  IAP") — carried on a co-lead's participation; NULL for a single-command lead. */
  agreement_note: z.string().nullable(),
  /** The Incident Commander / command officer (who to reach). */
  officer_name: z.string().nullable(),
  officer_phone: z.string().nullable(),
  officer_email: z.string().nullable(),
  officer_user_id: z.string().nullable(),
  joined_at: z.string(),
});
export type IncidentCommandParticipant = z.infer<typeof IncidentCommandParticipantSchema>;

/**
 * The kind of pending command-change carried on the incident_command_transfers rail (migration
 * 0147). A `transfer` is a §4.8 hand-over that MOVES the single lead A→B on acknowledge; a
 * `unified_invite` is a §4.7 co-command invite that ADDS a co-lead on accept. ONE ledger, ONE
 * acknowledge route branching on this discriminator (the #481 MUST-2 two-sided accept).
 */
export const COMMAND_HANDOVER_KINDS = ['transfer', 'unified_invite'] as const;
export const CommandHandoverKindSchema = z.enum(COMMAND_HANDOVER_KINDS);
export type CommandHandoverKind = z.infer<typeof CommandHandoverKindSchema>;

/**
 * A PENDING command-change on the shared incident_command_transfers rail (migration 0122; two-
 * sided 0147). Surfaced on the command block so the operator sees the pending hand-over / invite.
 * One owner throughout: nothing changes on the command until the counterparty accepts.
 *   • kind='transfer' (§4.8) — "Transfer of Command pending → <to org>" with the hand-over
 *     briefing; the from_agency remains lead until acknowledged.
 *   • kind='unified_invite' (§4.7) — "Unified Command invite pending → <to org>" with the signed
 *     agreement + optional Operations branch; NO co-lead until the invitee accepts.
 */
export const IncidentCommandTransferSchema = z.object({
  id: z.string(),
  /** transfer (§4.8, moves the lead) | unified_invite (§4.7, adds a co-lead on accept). */
  kind: CommandHandoverKindSchema,
  from_agency_id: z.string(),
  from_agency_name: z.string(),
  to_agency_id: z.string(),
  to_agency_name: z.string(),
  /** §4.8 the detailed hand-over briefing — present for a transfer, NULL for a unified invite. */
  handover_briefing: z.string().nullable(),
  /** §4.7 the signed agreement to unified command — present for a unified invite, NULL for a transfer. */
  agreement_note: z.string().nullable(),
  /** §4.7 the co-lead's Operations branch on a unified invite (NULL for a transfer). */
  functional_role: IncidentFunctionalRoleSchema.nullable(),
  to_officer_name: z.string().nullable(),
  to_officer_phone: z.string().nullable(),
  to_officer_email: z.string().nullable(),
  /** 'issued' pending · 'acknowledged' completed · 'cancelled' invalidated when command stood down.
   *  Only 'issued' is ever surfaced on the command block (queryPendingTransfer filters to it). */
  status: z.enum(['issued', 'acknowledged', 'cancelled']),
  issued_at: z.string(),
});
export type IncidentCommandTransfer = z.infer<typeof IncidentCommandTransferSchema>;

/**
 * One INCOMING command hand-over addressed to the viewer's agency (the RECEIVER's inbound worklist,
 * #481 MUST-1). The receiving jurisdiction had no inbound queue for a pending transfer/invite — it
 * could only be found on that incident's own panel — so this surfaces it in the Coordination inbox,
 * mirroring the agency-tasks target inbox. A row opens the incident record (the shipped
 * `#incident=<id>` deep-link) where the receiving IC accepts on the existing command-controls.
 */
export const CommandHandoverInboxItemSchema = z.object({
  /** The incident_command_transfers row id. */
  id: z.string(),
  kind: CommandHandoverKindSchema,
  incident_id: z.string(),
  incident_number: z.string(),
  incident_name: z.string(),
  hazard_type: z.string(),
  /** The issuing (current) lead organization — "from <agency>". */
  from_agency_id: z.string(),
  from_agency_name: z.string(),
  to_agency_id: z.string(),
  to_agency_name: z.string(),
  to_officer_name: z.string().nullable(),
  issued_at: z.string(),
});
export type CommandHandoverInboxItem = z.infer<typeof CommandHandoverInboxItemSchema>;

/**
 * The incident's INCIDENT COMMAND block (surfaced on IncidentDetail).
 *   • SINGLE command (§4.5, default) = one `lead` participation, `participants` = [lead],
 *     `unified` = false.
 *   • UNIFIED command (§4.7) = 2+ live `participants` sharing ONE command — exactly one
 *     `lead` (the Operations Section Chief) + co-leads carrying functional branches + a signed
 *     agreement; `unified` = true. NOT N ownerships (the §2.10 drift).
 */
export const IncidentCommandSchema = z.object({
  /** The single LEAD organization (the Operations Section Chief). NULL when not owned. */
  lead: IncidentCommandParticipantSchema.nullable(),
  /** All LIVE participations — the lead first, then unified co-leads (N≥1). */
  participants: z.array(IncidentCommandParticipantSchema),
  /** §4.7 unified command — true when 2+ lead organizations share command. */
  unified: z.boolean(),
  /** §4.8 a pending hand-over of command awaiting the receiving IC's acknowledgement. */
  pending_transfer: IncidentCommandTransferSchema.nullable(),
});
export type IncidentCommand = z.infer<typeof IncidentCommandSchema>;

/**
 * POST /api/incidents/:id/assume-command — ESTABLISH command (§4.5). Creates the lead
 * incident_command row AND advances the incident confirmed→owned in one act. The lead
 * organization must be a commanding jurisdiction seat (isCommandingSeat) — the server
 * re-validates it against the live agency; a coordinating EOC/PEOC/CAF is rejected.
 */
export const AssumeIncidentCommandSchema = z.object({
  /** The LEAD ORGANIZATION (an agency of a commanding jurisdiction). */
  agency_id: z.string().uuid(),
  /** The Incident Commander / command officer name (required — the "who owns this"). */
  officer_name: z.string().min(1).max(200),
  officer_phone: z.string().max(64).nullable().optional(),
  officer_email: z.string().email().max(320).nullable().optional(),
  officer_user_id: z.string().uuid().nullable().optional(),
  /** NULL for the single lead this slice (functional branches are next slice). */
  functional_role: IncidentFunctionalRoleSchema.nullable().optional(),
  session_id: z.string().min(1).max(128),
});
export type AssumeIncidentCommand = z.infer<typeof AssumeIncidentCommandSchema>;

/**
 * POST /api/incidents/:id/release-command — RELEASE a command participation.
 *   • agency_id OMITTED (or = the lead) — the single-command release: release the sole lead
 *     AND revert owned→confirmed. Refused (409) while unified co-leads remain (transfer the
 *     lead or have the co-leads leave first).
 *   • agency_id = a live NON-lead participant — that co-lead LEAVES unified command; the
 *     incident stays owned and returns toward single command (unified→false at N=1). This is
 *     the §4.7 "plan to return to single command" path.
 */
export const ReleaseIncidentCommandSchema = z.object({
  /** Which participation leaves — omit for the single-command lead release. */
  agency_id: z.string().uuid().optional(),
  session_id: z.string().min(1).max(128),
});
export type ReleaseIncidentCommand = z.infer<typeof ReleaseIncidentCommandSchema>;

/**
 * POST /api/incidents/:id/invite-unified-command — ISSUE a §4.7 UNIFIED COMMAND INVITE (the #481
 * MUST-2 two-sided accept, migration 0147). The current lead invites a 2nd+ commanding LEAD
 * ORGANIZATION to share command, carrying the Operations branch it would run and the REQUIRED
 * signed agreement ("the agreement to unified command should be verbally stated and signed in the
 * written IAP", §4.7). NO co-lead is created yet — §4.7 requires MUTUAL agreement, so co-command
 * establishes only when the INVITEE accepts (join_unified_command on the acknowledge-transfer
 * route). Supersedes the 0122 immediate join, which added a co-lead UNILATERALLY. The invited
 * organization must be a commanding jurisdiction seat (isCommandingSeat) and not already a live
 * participant, and there must be no pending command change on the incident.
 */
export const InviteToUnifiedCommandSchema = z.object({
  /** The invited LEAD ORGANIZATION (a commanding jurisdiction, ≠ the current lead). */
  agency_id: z.string().uuid(),
  /** The signed agreement to unified command (§4.7 — REQUIRED, verbally stated + signed in the IAP). */
  agreement_note: z.string().min(1).max(2000),
  /** The Operations branch this lead organization would run (Suppression / Community Protection / …). */
  functional_role: IncidentFunctionalRoleSchema.nullable().optional(),
  /** The invited co-lead's Incident Commander / command officer. */
  officer_name: z.string().min(1).max(200),
  officer_phone: z.string().max(64).nullable().optional(),
  officer_email: z.string().email().max(320).nullable().optional(),
  officer_user_id: z.string().uuid().nullable().optional(),
  session_id: z.string().min(1).max(128),
});
export type InviteToUnifiedCommand = z.infer<typeof InviteToUnifiedCommandSchema>;

/**
 * POST /api/incidents/:id/transfer-command — ISSUE a §4.8 TRANSFER OF COMMAND: the outgoing
 * lead hands the single lead over to another commanding organization with a REQUIRED detailed
 * hand-over briefing ("If the incident commander changes, a detailed hand-over briefing is
 * required.", §4.8). Command does NOT move yet — the from_agency remains lead until the
 * receiving IC acknowledges (one owner throughout). At most one pending transfer at a time.
 */
export const TransferIncidentCommandSchema = z.object({
  /** The RECEIVING lead organization (a commanding jurisdiction, ≠ the current lead). */
  to_agency_id: z.string().uuid(),
  /** §4.8 the detailed hand-over briefing (REQUIRED — a transfer is a briefing, not a bare ack). */
  handover_briefing: z.string().min(1).max(4000),
  /** The receiving Incident Commander / command officer (who holds command after hand-over). */
  to_officer_name: z.string().min(1).max(200),
  to_officer_phone: z.string().max(64).nullable().optional(),
  to_officer_email: z.string().email().max(320).nullable().optional(),
  session_id: z.string().min(1).max(128),
});
export type TransferIncidentCommand = z.infer<typeof TransferIncidentCommandSchema>;

/**
 * POST /api/incidents/:id/acknowledge-transfer — the RECEIVING/INVITED incident commander ACCEPTS
 * the pending command change. The route branches on the pending row's kind (migration 0147):
 *   • kind='transfer' (§4.8) — the transfer is stamped acknowledged AND the lead moves A → B (the
 *     from_agency's lead participation is released; the to_agency is promoted-or-created as the sole
 *     lead), keeping the uq_incident_one_lead invariant. One owner before, one after.
 *   • kind='unified_invite' (§4.7) — the invite is stamped acknowledged AND the invitee is INSERTed
 *     as a co-lead (is_lead=false, unified=true) with its Operations branch + the signed agreement;
 *     the whole command becomes unified. The co-command establishes only HERE, on mutual accept.
 * The body is the same for both (just the session); the pending row supplies the rest.
 */
export const AcknowledgeIncidentTransferSchema = z.object({
  session_id: z.string().min(1).max(128),
});
export type AcknowledgeIncidentTransfer = z.infer<typeof AcknowledgeIncidentTransferSchema>;
